
/**
 * Supabase Client Configuration
 * 
 * This client supports multiple environments:
 * 1. Production: Uses environment variables (VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY)
 * 2. Lovable: Uses managed credentials through Lovable's Supabase integration
 * 3. Local Development: Uses .env file
 * 
 * Security Note: The fallback values are only used in Lovable's managed environment
 * where credentials are securely injected through their integration system.
 * In production, always use environment variables.
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import logger from "@/utils/logger";

// Safely access environment variables with Lovable-aware fallbacks
const getEnvVar = (key: string, lovableFallback?: string): string | undefined => {
  try {
    // @ts-expect-error - Accessing import.meta.env which may not exist in all environments - Access import.meta safely
    const envValue = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key];
    if (envValue) return envValue;
    
    // In Lovable environment, use managed fallback values
    // These are injected by Lovable's integration system
    if (lovableFallback && typeof window !== 'undefined') {
      // Only use fallback in browser environment (not SSR)
      return lovableFallback;
    }
    
    return undefined;
  } catch (e) {
    logger.error(`Error accessing environment variable ${key}:`, e);
    return lovableFallback;
  }
};

// Get Supabase configuration
// In production, these should come from environment variables
// In Lovable, these are managed through the Supabase integration
const supabaseUrl = getEnvVar('VITE_SUPABASE_URL', 
  // Lovable-managed value (only used in Lovable environment)
  'https://iorusbjzbltyjalyvlyu.supabase.co'
);

const supabaseAnonKey = getEnvVar('VITE_SUPABASE_ANON_KEY',
  // Lovable-managed value (only used in Lovable environment)
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlvcnVzYmp6Ymx0eWphbHl2bHl1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM1MDg3MTQsImV4cCI6MjA1OTA4NDcxNH0.ScBnr0gZI7stXLle9X2gtc6AhLxOaXKze2orx7kkec0'
);

// Validate configuration
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase configuration. Please ensure environment variables are set or Supabase integration is configured in Lovable.'
  );
}

// Additional validation for URL format
if (!supabaseUrl.includes('supabase.co')) {
  throw new Error('Invalid Supabase URL format. URL must be a valid Supabase project URL.');
}

// Create Supabase client
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Export the URL for external reference (safe to expose)
export const getSupabaseUrl = () => supabaseUrl;
